import { ClassValue, tv, type VariantProps } from "tailwind-variants";
import { SlotsToClasses } from "@nextui-org/theme";
import { Divider } from "@nextui-org/react";
import Button, { ButtonProps } from "../Button";
import { forwardRef } from "react";
import { cn } from "@networx/utils/tailwind/cn";

export default forwardRef(Card);
function Card({
  children,
  className, classNames,
  footer, footerProps, header, subtitle,
  ...tvProps
}: Props, ref) {
  const style = card(tvProps);
  return (
    <div
      ref={ref}
      className={style.base({ className: [className, classNames?.base] })}
    >
      {header && (
        <div className={style.headerWrapper({ className: [classNames?.headerWrapper] })}>
          <div className={style.header({ className: [classNames?.header] })}>{header}</div>
          {subtitle && <div className={style.subtitle({ className: [classNames?.subtitle] })}>{subtitle}</div>}
        </div>
      )}
      <div className={style.content({ className: [classNames?.content] })}>
        {children}
      </div>
      {(footer || !footerProps?.hide) && (
        <div className={style.footerWrapper({ className: [classNames?.footerWrapper] })}>
          {footer && footer}
          {!footer && (
            <>
              {!footerProps?.hideDivider && <Divider />}
              <div className={style.footer({ className: [classNames?.footer] })}>
                {footerProps?.previousButton !== undefined ?
                  footerProps.previousButton
                  : <Button
                    variant="clear"
                    {...footerProps?.previousButtonProps}
                    className={cn("min-w-[117px] text-[14px]", footerProps?.previousButtonProps?.className)}
                  >
                    Previous
                  </Button>
                }
                {footerProps?.nextButton !== undefined ?
                  footerProps.nextButton
                  : <Button
                    {...footerProps?.nextButtonProps}
                    className={cn("min-w-[117px] text-[18px]", footerProps?.nextButtonProps?.className)}
                  >
                    Next
                  </Button>
                }
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const card = tv({
  slots: {
    base: [
      "flex flex-col justify-center items-center gap-4",
      "pt-10 px-0",
      "rounded-small",
      "bg-[#fbfbfb]",
      "border border-default",
    ],
    headerWrapper: ["w-full flex flex-col gap-4"],
    header: ["text-center text-[28px] font-black text-primary-800"],
    subtitle: ["text-foreground text-medium text-center"],
    content: ["flex flex-col justify-center items-center gap-4 p-4"],
    footerWrapper: ["w-[90%] p-[20px]", "flex flex-col gap-4 justify-center items-center"],
    footer: ["flex gap-4 min-h-11"],
  },
  variants: {},
  defaultVariants: {},
});

export type CardProps = VariantProps<typeof card>;

type FooterProps = {
  hide: boolean;
  hideDivider: boolean;
  previousButton: React.ReactNode;
  previousButtonProps: ButtonProps;
  nextButton: React.ReactNode;
  nextButtonProps: ButtonProps;
};

interface Props extends CardProps {
  header?: React.ReactNode;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  footerProps?: Partial<FooterProps>;
  className?: ClassValue;
  classNames?: SlotsToClasses<keyof ReturnType<typeof card>>;
}
